import React, { useEffect, useState } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { getShouldUseCPURendering } from '@cornerstonejs/core';
import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { RestartAlt } from '@mui/icons-material';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import InvertColorsIcon from '@mui/icons-material/InvertColors';
import RotateIcon from '@mui/icons-material/Refresh';
import TonalityIcon from '@mui/icons-material/Tonality';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { IconButton, Tooltip } from '@mui/material';
import Slider from '@mui/material/Slider';
import type { Theme } from '@mui/material/styles';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import useKeypress from 'react-use-keypress';

import { InfoOutlinedIcon } from '../../../../common-components/elements/VideaIcons/VideaIcons';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  DEFAULT_BRIGHTNESS_VALUE,
  DEFAULT_CONTRAST_VALUE,
  MAX_BRIGHTNESS_VALUE,
  MAX_CONTRAST_VALUE,
  MIN_BRIGHTNESS_VALUE,
  MIN_CONTRAST_VALUE,
} from '../../../../constants';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import { usePreferences } from '../../../../react-query/usePreferences';
import { theme } from '../../../../theme/theme';
import {
  blueBlack400,
  blueUltramarine100,
  greyLight700,
} from '../../../../theme/videa-style-tokens';
import { isValidToothNumber } from '../../../../utils/finding';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { selectTeethAtom } from '../../../patientList/atoms';
import { isWebImageType } from '../../annotation-utils/annotation-utils';
import AnnotatorConstants from '../../annotator.constants';
import { latestImagesVisitDateAtom, showPastVisitsAtom } from '../../atoms';
import { disableKeyPressAtom } from '../../casePresentation/atoms';
import { ToothLabel } from '../../casePresentation/toolbar/CasePresentationImageTracker';
import CurrentImageTracker from '../../currentImageTracker/CurrentImageTracker';
import { ConfidenceScoreFilterV2 } from '../../findingSidebar/confidenceScoreFilter/ConfidenceScoreFilterV2';
import {
  RerunAiForImageSharpening,
  RerunAiForNewCariesModel,
} from '../../findingSidebar/RerunAi';
import ImageFilterSelector from '../../imageFilterSelector/ImageFilterSelector';
import type { FindingTypes, ImageObject } from '../../types';
import { ALL_KEYS, KEY_MAP } from '../../utils';
import {
  imageBrowserExpandedAtom,
  imageBrowserOpenAtom,
} from '../findingViewer/imageBrowser/atoms';
import { useTrackImageIndex } from '../findingViewer/imageBrowser/helpers';
import { ImageBrowser } from '../findingViewer/imageBrowser/ImageBrowser';
import { Shortcuts } from '../findingViewer/Shortcuts';
import { useRotation } from '../RotationContext';

import { hideFindingsAtom } from './atoms';
declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

const ToolbarContainer = styled.div`
  display: flex;
  align-items: top;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 1em;

  &.image-controls-container {
    align-items: center;
    padding: 1em;

    @media (max-width: 1200px) {
      font-size: 0.925em;
    }

    @media (max-width: 800px) {
      font-size: 0.85em;
    }
  }

  &.image-controls-with-browser {
    align-items: center;
    padding: 1em;
    margin-bottom: 7em;

    @media (max-width: 1200px) {
      font-size: 0.925em;
    }

    @media (max-width: 800px) {
      font-size: 0.85em;
    }
  }

  .MuiToggleButtonGroup-root {
    padding: 0;
  }

  &.multi-file-toolbar {
    padding: 0 1em;
    box-sizing: border-box;
    margin: 0;
    height: 4em;
  }
  .image-controls {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.75em;
    padding-left: 0.75em;

    .viewer-compare & {
      justify-content: center;
    }
  }
  .sliders-container {
    padding: 0.75em;
    width: 13em;
    display: flex;
    justify-content: center;
    flex-direction: row;
    position: absolute;
    background-color: ${theme.palette.secondary.dark};
    border-radius: 0.25em;
    &.with-browser {
      bottom: 11em;
    }
    &.without-browser {
      bottom: 4em;
    }
  }
  .MuiIconButton-root {
    padding: 0;
    font-size: 1.25em;
  }
  .MuiSvgIcon-root {
    width: 1em;
    height: 1em;
    font-size: 1.125em;
  }
  .white {
    color: ${theme.palette.common.white};
  }
  .disabled {
    color: ${greyLight700} !important;
  }
  .selected-icon-container {
    background: ${theme.palette.secondary.main};
  }
  .MuiSlider-thumb {
    // Unsure why but it seems w/ our custom brightness styling the thumb is just a bit off
    // Feel free to remove once we replace the slider styling.
    margin-right: -10px;
    margin-left: 0;
  }
  .visit-date {
    margin-left: 0.125em;
    color: ${blueBlack400};
    font-size: 0.75em;
    max-width: 11ch;
    min-width: 11ch;
  }
  .toolbar-mid {
    display: flex;
    justify-content: center;
    flex-grow: 1;
  }
  .toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5em;
    @media (max-width: 1200px) {
      margin-right: 1em;
    }

    .MuiButtonBase-root {
      font-size: inherit;

      @media (max-width: 800px) {
        font-size: 0.75em;
      }
    }

    .toolbar-visit {
      font-size: 1em;
      color: ${blueUltramarine100};
      width: 10ch;
      text-align: right;
      line-height: 1;

      .MuiSvgIcon-root {
        font-size: 1em;
        margin-right: 0.25em;
        transform: translateY(0.25em);
      }
    }
  }
`;

const ToolbarItem = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 0.25em;
  height: 2em;
  width: 2em;
  border-radius: 0.125em;

  &:hover {
    background: ${theme.palette.secondary.main};
  }
`;

interface Props {
  activeImage: ImageObject;
  isUsingLatestModel?: boolean;
  estimatedVisitId?: number;
  enabledFindingTypes: FindingTypes;
  onSetEnabledFindingTypes: React.Dispatch<React.SetStateAction<FindingTypes>>;
  isOpacityInverted: boolean;
  viewerMode: number;
  currentImageFilter: string;
  changeViewerMode: (newMode: number) => any;
  magnifyAnchorRef: (el: any) => void;
  rotateImage: (image: any) => any;
  rotatedImages: Record<string, number>;
  isHQEnabled: boolean;
  onSetIsHQEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  toggleOpacity: () => any;
  contrastValue: number;
  onSetContrastValue: React.Dispatch<React.SetStateAction<number>>;
  brightnessValue: number;
  onSetBrightnessValue: React.Dispatch<React.SetStateAction<number>>;
  onSetShouldPersistUserModifications: React.Dispatch<
    React.SetStateAction<boolean>
  >;

  showImageTools: boolean;
  lastAnalysisDate?: Date;
  currentPatient: any;
  onLeavePatient: () => void;
  currentImageIndex: number;
  patients: any;
  patientVisitsData: any;
  imageKeysToDisplay: any[] | undefined;
  sortedAndFilteredImages: any[] | undefined;
  user: any;
  sidebarIsNotCollapsed: boolean;
  onExpandSidebar: (isNotCollapsed: boolean) => void;
  numberOfSortedFMXImages: number;
  onSetCurrentImageFilter: () => void;
  numberOfBitewings: number;
  onUpdateOdontogramView: (isVisible: boolean) => void;
  selectedPatientId: string;
}

export const ImageToolbar: React.FC<Props> = ({
  activeImage,
  isUsingLatestModel,
  estimatedVisitId,
  brightnessValue,
  changeViewerMode,
  contrastValue,
  currentImageFilter,
  onExpandSidebar,
  isHQEnabled: hqEnabled,
  isOpacityInverted,
  lastAnalysisDate,
  magnifyAnchorRef,
  rotatedImages,
  rotateImage,
  onSetBrightnessValue,
  onSetContrastValue,
  onSetCurrentImageFilter,
  onSetIsHQEnabled,
  onSetShouldPersistUserModifications,
  showImageTools,
  toggleOpacity,
  viewerMode,
  numberOfSortedFMXImages,
  numberOfBitewings,
  onUpdateOdontogramView,
  sidebarIsNotCollapsed,
  sortedAndFilteredImages,
  selectedPatientId,
}) => {
  const [isBrightnessIconSelected, setisBrightnessIconSelected] =
    useState(false);
  const [isContrastIconSelected, setisContrastIconSelected] = useState(false);
  const [showPastVisits] = useAtom(showPastVisitsAtom);
  const [hideFindings, setHideFindings] = useAtom(hideFindingsAtom);
  const visitDate = useAtomValue(latestImagesVisitDateAtom);
  const { t } = useLingui();

  const history = useHistory();

  const { trackEvent } = useTrackEvent();

  const {
    resetImageRotation,
    currentlyRotatingImages,
    resetImageRotationState,
  } = useRotation();

  const { data } = usePreferences();
  const isCdnEnabled = !!data?.cdnEnabled;
  const isHQDisabledByDefault = Boolean(data?.hqOffByDefault);
  const isHQDisabledInFmx =
    !!data?.enableHqOnThumbnails && !isHQDisabledByDefault;
  const isImageBrowserOpen = useAtomValue(imageBrowserOpenAtom);
  const isImageBrowserExpanded = useAtomValue(imageBrowserExpandedAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const dateFNSLocale = useGetDateFNSLocale();
  const disableKeyPress = useAtomValue(disableKeyPressAtom);

  // Need to default the HQ value once we get prefs back
  useEffect(() => {
    // Wait for preferences + active image to be defined before setting HQ toggle
    // Don't reset the HQ toggle once it's been set
    if (
      !data ||
      (!activeImage?.imageId && !data.enableHqOnThumbnails) ||
      hqEnabled !== undefined
    ) {
      return;
    }
    let isHQDefault = !data?.hqOffByDefault;
    if (activeImage?.userModifications) {
      isHQDefault =
        activeImage?.userModifications?.hqEnabled ?? !data?.hqOffByDefault;
    }

    onSetIsHQEnabled(isHQDefault);
    // Don't want to trigger effect anytime activeImage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeImage?.userModifications?.hqEnabled,
    activeImage?.imageId,
    data,
    onSetIsHQEnabled,
  ]);

  const nextImageIds = useTrackImageIndex();

  const toggleHighImageQuality = () => {
    if (isHQButtonDisabled) {
      return;
    }
    const isNewHQEnabled = !isHQEnabled;
    onSetShouldPersistUserModifications(true);
    onSetIsHQEnabled(isNewHQEnabled);
    trackEvent(mixpanelEvents.hqToggle, {
      isHQEnabled: isNewHQEnabled,
      source: `${
        !activeImage ? 'multiFileViewer' : 'singleImageViewer'
      } - toolbar`,
    });
  };

  const { source } = useSelector((state: RootStateOrAny) => state?.external);

  let isHQEnabled = hqEnabled;
  let isHQButtonDisabled = false;

  let hqTooltipTitle = isHQEnabled
    ? t`Show original image (H)`
    : t`Show sharpened image (H)`;

  if (!isCdnEnabled && !activeImage?.imageKeys?.generalized?.key) {
    hqTooltipTitle = t`Sharpened image not available`;
    isHQEnabled = false;
    isHQButtonDisabled = true;
  }
  if (
    !isCdnEnabled &&
    !activeImage?.imageKeys?.sharpened?.key &&
    (!activeImage?.imageKeys?.original?.key ||
      !isWebImageType(activeImage.imageKeys.original.key))
  ) {
    hqTooltipTitle = t`Original image not available`;
    isHQEnabled = true;
    isHQButtonDisabled = true;
  }

  const isToolbarSettingsNotDefault =
    hideFindings ||
    brightnessValue !== DEFAULT_BRIGHTNESS_VALUE ||
    contrastValue !== DEFAULT_CONTRAST_VALUE ||
    isOpacityInverted ||
    isHQEnabled === !!isHQDisabledByDefault ||
    !!rotatedImages[activeImage?.objectId] ||
    (showPastVisits && !!currentlyRotatingImages?.imageIds.length);

  const resetToolbarSettings = () => {
    if (!isToolbarSettingsNotDefault || disableKeyPress) {
      return;
    }
    onSetBrightnessValue(DEFAULT_BRIGHTNESS_VALUE);
    onSetContrastValue(DEFAULT_CONTRAST_VALUE);
    onSetIsHQEnabled(!isHQDisabledByDefault);
    if (isOpacityInverted) {
      toggleOpacity();
    }
    if (showPastVisits) {
      resetImageRotationState();
    } else {
      resetImageRotation(activeImage?.objectId);
    }
    if (hideFindings) {
      setHideFindings(false);
    }
    onSetShouldPersistUserModifications(true);
  };

  useKeypress(['D', 'd'], resetToolbarSettings);
  useKeypress(ALL_KEYS, (event: { key: any }) => {
    if (disableKeyPress) return;
    switch (true) {
      case KEY_MAP.keyPressedNextImage.includes(event.key):
        moveIndexBy(nextImageIds?.next);
        break;
      case KEY_MAP.keyPressedPrevImage.includes(event.key):
        moveIndexBy(nextImageIds?.prev);
        break;
    }
  });

  const moveIndexBy = (nextImageId?: string) => {
    if (!nextImageId) {
      return;
    }
    let newRoute = `${history.location.pathname}/${nextImageId}`;
    if (activeImage) {
      newRoute = history.location.pathname.replace(
        activeImage.imageId,
        nextImageId || '',
      );
    }
    history.push({ pathname: newRoute, search: history.location.search });
  };

  const activeImageObj = {
    imageQuality: { value: 'acceptable' },
    ...activeImage,
  };

  const toolbarStyle =
    isImageBrowserOpen && !isImageBrowserExpanded
      ? 'image-controls-with-browser'
      : 'image-controls-container';

  const sliderStyle =
    isImageBrowserOpen && !isImageBrowserExpanded
      ? 'sliders-container with-browser'
      : 'sliders-container without-browser';

  return (
    <ToolbarContainer className={toolbarStyle}>
      {source !== 'DDAI' &&
        !showImageTools &&
        isHQDisabledInFmx &&
        activeImage?.detectedImageType !== 'COLOR_IMAGE' && (
          <div className="image-controls">
            <ToolbarItem>
              <IconButton
                aria-label={t`High Quality`}
                className="white"
                onClick={toggleHighImageQuality}
              >
                <Tooltip title={hqTooltipTitle} disableInteractive>
                  <HighQualityIcon
                    style={{
                      color: isHQEnabled
                        ? theme.palette.success.light
                        : 'white',
                    }}
                  />
                </Tooltip>
              </IconButton>
            </ToolbarItem>
          </div>
        )}
      {showImageTools && (
        <div className="image-controls">
          <IconButton
            aria-label={t`Reset Toolbar Settings`}
            className={classNames([
              'sentry-unmask',
              {
                disabled: !isToolbarSettingsNotDefault,
                white: isToolbarSettingsNotDefault,
              },
            ])}
            onClick={resetToolbarSettings}
          >
            <Tooltip title={t`Reset Toolbar Settings [ D ]`}>
              <RestartAlt />
            </Tooltip>
          </IconButton>
          {(isContrastIconSelected || isBrightnessIconSelected) && (
            <div className={sliderStyle}>
              {isContrastIconSelected ? (
                <Slider
                  value={contrastValue}
                  marks={[{ value: DEFAULT_CONTRAST_VALUE, label: '' }]}
                  // @ts-expect-error TS(2769): No overload matches this call.
                  onChange={(_, newValue: number) => {
                    onSetShouldPersistUserModifications(true);
                    onSetContrastValue(newValue);
                  }}
                  min={MIN_CONTRAST_VALUE}
                  step={1}
                  max={MAX_CONTRAST_VALUE}
                  valueLabelDisplay="off"
                  className="white"
                />
              ) : (
                isBrightnessIconSelected && (
                  <Slider
                    value={brightnessValue}
                    marks={[{ value: DEFAULT_BRIGHTNESS_VALUE, label: '' }]}
                    // @ts-expect-error TS(2769): No overload matches this call.
                    onChange={(_, newValue: number) => {
                      onSetShouldPersistUserModifications(true);
                      onSetBrightnessValue(newValue);
                    }}
                    min={MIN_BRIGHTNESS_VALUE}
                    step={1}
                    max={MAX_BRIGHTNESS_VALUE}
                    track="inverted"
                    valueLabelDisplay="off"
                    className="white"
                  />
                )
              )}
            </div>
          )}

          {showPastVisits && (
            <IconButton
              aria-label={t`Show Findings`}
              color={!hideFindings ? 'primary' : 'default'}
              className={classNames([
                'sentry-unmask',
                {
                  white: hideFindings,
                },
              ])}
              onClick={() => setHideFindings(!hideFindings)}
            >
              <Tooltip title={t`Show Findings`}>
                <VisibilityIcon />
              </Tooltip>
            </IconButton>
          )}
          <ToolbarItem
            // @ts-expect-error TS(2322): Type 'false | "selected-icon-container"' is not as... Remove this comment to see the full error message
            className={isBrightnessIconSelected && 'selected-icon-container'}
          >
            <IconButton
              aria-label={t`Brightness`}
              className={classNames([
                'sentry-unmask',
                {
                  disabled: getShouldUseCPURendering(),
                  white: !getShouldUseCPURendering(),
                },
              ])}
              onClick={() => {
                setisBrightnessIconSelected((prev) => !prev);
                if (isContrastIconSelected) setisContrastIconSelected(false);
              }}
              // Only GPU rendering should set invert/brightness/contrast for now
              disabled={getShouldUseCPURendering()}
            >
              <Tooltip title={t`Brightness`} disableInteractive>
                <WbSunnyIcon />
              </Tooltip>
            </IconButton>
          </ToolbarItem>
          <ToolbarItem
            // @ts-expect-error TS(2322): Type 'false | "selected-icon-container"' is not as... Remove this comment to see the full error message
            className={isContrastIconSelected && 'selected-icon-container'}
          >
            <IconButton
              aria-label={t`Contrast`}
              className={classNames([
                'sentry-unmask',
                {
                  disabled: getShouldUseCPURendering(),
                  white: !getShouldUseCPURendering(),
                },
              ])}
              onClick={() => {
                setisContrastIconSelected((prev) => !prev);
                if (isBrightnessIconSelected)
                  setisBrightnessIconSelected(false);
              }}
              // Only GPU rendering should set invert/brightness/contrast for now
              disabled={getShouldUseCPURendering()}
            >
              <Tooltip title={t`Contrast`} disableInteractive>
                <TonalityIcon />
              </Tooltip>
            </IconButton>
          </ToolbarItem>
          <ToolbarItem
            // @ts-expect-error TS(2322): Type 'false | "selected-icon-container"' is not as... Remove this comment to see the full error message
            className={isOpacityInverted && 'selected-icon-container'}
          >
            <IconButton
              aria-label={t`Opacity`}
              onClick={toggleOpacity}
              className={classNames([
                'sentry-unmask',
                {
                  disabled: getShouldUseCPURendering(),
                  white: !getShouldUseCPURendering(),
                },
              ])}
              // Only GPU rendering should set invert/brightness/contrast for now
              disabled={getShouldUseCPURendering()}
            >
              <Tooltip title={t`Invert colors (I)`} disableInteractive>
                <InvertColorsIcon />
              </Tooltip>
            </IconButton>
          </ToolbarItem>
          <ToolbarItem
            // @ts-expect-error TS(2322): Type 'false | "selected-icon-container"' is not as... Remove this comment to see the full error message
            className={
              viewerMode === AnnotatorConstants.MODES.MAGNIFYING &&
              'selected-icon-container'
            }
          >
            <IconButton
              aria-label={t`Magnifying Tool`}
              className="white sentry-unmask"
              onClick={() =>
                changeViewerMode(
                  viewerMode === AnnotatorConstants.MODES.MAGNIFYING
                    ? AnnotatorConstants.MODES.VIEWING
                    : AnnotatorConstants.MODES.MAGNIFYING,
                )
              }
              ref={magnifyAnchorRef}
            >
              <Tooltip title={t`Magnify (Z)`} disableInteractive>
                <ZoomInIcon />
              </Tooltip>
            </IconButton>
          </ToolbarItem>
          {!showPastVisits && (
            <ToolbarItem
              // @ts-expect-error TS(2322): Type '0 | "selected-icon-container"' is not assign... Remove this comment to see the full error message
              className={
                rotatedImages[activeImage?.objectId] &&
                'selected-icon-container'
              }
            >
              <IconButton
                aria-label={t`Rotate Image`}
                className="white sentry-unmask"
                onClick={() => rotateImage(activeImage)}
              >
                <Tooltip title={t`Rotate image (.)`} disableInteractive>
                  <RotateIcon />
                </Tooltip>
              </IconButton>
            </ToolbarItem>
          )}
          {(source !== 'DDAI' || data?.DDAISharpeningEnabled) &&
            activeImage?.detectedImageType !== 'COLOR_IMAGE' && (
              <ToolbarItem>
                <IconButton
                  aria-label={t`High Quality`}
                  className="white sentry-unmask"
                  onClick={toggleHighImageQuality}
                >
                  <Tooltip title={hqTooltipTitle} disableInteractive>
                    <HighQualityIcon
                      style={{
                        color: isHQEnabled
                          ? theme.palette.success.light
                          : 'white',
                      }}
                    />
                  </Tooltip>
                </IconButton>
              </ToolbarItem>
            )}
          {showPastVisits && <ConfidenceScoreFilterV2 image={null} />}
          <Shortcuts />
        </div>
      )}
      <RerunAiForNewCariesModel
        estimatedVisitId={estimatedVisitId}
        isUsingLatestModel={isUsingLatestModel}
      />

      <div className="toolbar-mid">
        {activeImage && (
          <CurrentImageTracker
            currentImageFilter={currentImageFilter}
            currentImageIndex={nextImageIds?.currentIndex}
            totalImages={nextImageIds?.totalImages}
            onPreviousClick={() => moveIndexBy(nextImageIds?.prev)}
            onNextClick={() => moveIndexBy(nextImageIds?.next)}
            activeImage={activeImage}
          />
        )}
      </div>
      <div className="toolbar-right">
        {!activeImage && selectedTeeth.length && <ToothLabel />}
        <span className="toolbar-visit">
          {!showPastVisits && lastAnalysisDate && (
            <Tooltip
              title={
                <VideaTypography variant="labelSmall">
                  <Trans>Date of last AI analysis: </Trans>
                  {format(lastAnalysisDate, 'dd-MMM-yyyy', {
                    locale: dateFNSLocale,
                  })}
                </VideaTypography>
              }
              arrow
            >
              <InfoOutlinedIcon />
            </Tooltip>
          )}
          {!showPastVisits && visitDate && (
            <VideaTypography variant="labelSmall" className="visit-date">
              <Trans>Visit Date:</Trans>{' '}
              {format(new Date(visitDate), 'MMM-dd-yyyy', {
                locale: dateFNSLocale,
              })}
            </VideaTypography>
          )}
        </span>
        {!showPastVisits && !activeImage && (
          <ImageFilterSelector
            activeImage={activeImageObj}
            numberOfSortedFMXImages={numberOfSortedFMXImages}
            currentImageFilter={currentImageFilter}
            numberOfBitewings={numberOfBitewings}
            sidebarIsNotCollapsed={sidebarIsNotCollapsed}
            sortedAndFilteredImages={sortedAndFilteredImages}
            onSetCurrentImageFilter={onSetCurrentImageFilter}
            onExpandSidebar={onExpandSidebar}
            onUpdateOdontogramView={onUpdateOdontogramView}
          />
        )}

        {currentImageFilter !== AnnotatorConstants.ODONTOGRAM &&
          !isValidToothNumber(currentImageFilter) &&
          !showPastVisits && <ImageBrowser />}
        {sortedAndFilteredImages && estimatedVisitId && (
          <RerunAiForImageSharpening
            estimatedVisitId={estimatedVisitId}
            images={sortedAndFilteredImages}
            selectedPatientId={selectedPatientId}
          />
        )}
      </div>
    </ToolbarContainer>
  );
};
